import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useMutation } from '@apollo/client';
import { Modal, ModalContent, useDisclosure } from '@nextui-org/react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { pdf } from '@react-pdf/renderer';
import { ArrowLeft } from 'lucide-react';

import { lockedWorkoutIds } from '../utils/workoutLock';
import { CREATE_WORKOUT, UPDATE_WORKOUT } from '../graphql/mutations';
import { trainingNames } from '../utils/templates';
import { trackPageView, trackEvent } from '../utils/gt';
import { downloadBlob, sanitizeFilename } from '../utils/downloadBlob';
import { trialContext } from '../utils/subscription';

import { WorkoutEditorView } from '../components/workouts/WorkoutEditorView';
import { WorkoutBuilder } from '../components/workouts/WorkoutBuilder';
import { WorkoutsListPane } from '../components/workouts/WorkoutsListPane';
import { ExerciseLibraryPane } from '../components/workouts/ExerciseLibraryPane';
import { useExerciseLibrary, useWorkoutActions, useWorkoutsData } from '../components/workouts/useWorkouts';
import { libraryFields } from '../components/workouts/exerciseUtils';
import { ModalUpgradePro } from '../components/ModalUpgradePro';
import { WorkoutPDF } from '../components/WorkoutPDF';

import saveAiCustomVidEn from '../assets/pro-only-vids/save-ai-custom-en.mp4';
import saveAiCustomVidBr from '../assets/pro-only-vids/save-ai-custom-br.mp4';
import saveAiSplitVidEn from '../assets/pro-only-vids/save-ai-split-en.mp4';
import saveAiSplitVidBr from '../assets/pro-only-vids/save-ai-split-br.mp4';

const newExercise = (language) => ({
  name: '',
  series: '3',
  reps: '12',
  interval: 60,
  howTo: '',
  weight: '',
  weightUnit: language === 'pt' ? 'kg' : 'lbs',
  videoUrl: '',
  thumbnailUrl: '',
  gifUrl: '',
  exerciseId: '',
  warmup: false,
});

// A new workout starts without exercises: they come from the library (saving asks for at least one)
const emptyWorkout = () => ({
  name: 'A',
  description: '',
  image: 'workout0',
  motivation: true,
  voiceInstructions: true,
  exercises: [],
});

// Rows need stable keys across deletes and swaps; the key never leaves the page
let exerciseKeySeed = 0;
const withKey = (exercise) => (exercise.uiKey ? exercise : { ...exercise, uiKey: `exercise-${(exerciseKeySeed += 1)}` });
const withoutKeys = (workoutData) => ({
  ...workoutData,
  exercises: workoutData.exercises.map(({ uiKey: _uiKey, ...exercise }) => exercise),
});

/**
 * The workout editor and the new-workout builder (A · Índice). Desktop keeps Meus Treinos beside it, and the library
 * takes that pane while exercises are added; phones get a page with a save bar, and the library as a page over it.
 * Leaving with changes asks first.
 */
const WorkoutPage = () => {
  const { t, i18n: { language } } = useTranslation();
  const isPt = language === 'pt';
  const editor = (key, options) => t(`pages.Workout.editor.${key}`, options);
  const navigate = useNavigate();
  const { workoutId } = useParams();
  const location = useLocation();
  const isDesktop = useMediaQuery({ query: '(min-width: 960px)' });
  const isNew = !workoutId;

  const data = useWorkoutsData();
  const { workouts, rows, me, isPro, limit, usable, isLoading, refetchWorkouts } = data;
  const actions = useWorkoutActions(data);
  const library = useExerciseLibrary();

  // From the list's empty desktop pane: how to build it was already picked there
  const start = location.state?.start ?? (location.state?.skipModal ? 'zero' : null);
  const builderStepFor = (how) => (how === 'ai' ? 'muscles' : how === 'split' ? 'split' : 'choose');
  const [stage, setStage] = useState(() => (!isNew || start === 'zero' ? 'editor' : 'build'));
  const [builderStart, setBuilderStart] = useState(() => (me && isPro ? builderStepFor(start) : 'choose'));

  const [workoutData, setWorkoutData] = useState(emptyWorkout);
  const [takenLetters, setTakenLetters] = useState(null);
  const [isAIGenerated, setIsAIGenerated] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setDirty] = useState(false);
  const [descriptionMissing, setDescriptionMissing] = useState(false);
  const [missingNames, setMissingNames] = useState(false);
  const [hasNoExercises, setHasNoExercises] = useState(false);
  const [openKey, setOpenKey] = useState(null);
  // The library: null, 'add' (stays open to add several) or 'swap' (replaces swapIndex)
  const [libMode, setLibMode] = useState(start === 'zero' ? 'add' : null);
  const [swapIndex, setSwapIndex] = useState(null);
  const [addedCount, setAddedCount] = useState(0);
  const [splitLimitInfo, setSplitLimitInfo] = useState(null);
  // What to do once the person agrees to drop their changes
  const [pendingLeave, setPendingLeave] = useState(null);
  const descriptionRef = useRef(null);

  const aiOffer = useDisclosure();
  const splitOffer = useDisclosure();
  const splitLimit = useDisclosure();

  const [createWorkoutMutation] = useMutation(CREATE_WORKOUT);
  const [updateWorkoutMutation] = useMutation(UPDATE_WORKOUT);

  useEffect(() => {
    trackPageView('workout', `${t('appUrl')}/workout`, '/workout');
  }, [t]);

  // The workout (or a new one on the first free letter) once the list is known
  useEffect(() => {
    if (!workouts || takenLetters) return;
    const takenNames = workouts.map((workout) => workout.attributes.name);
    if (isNew) {
      setWorkoutData((prev) => ({ ...prev, name: trainingNames.find((letter) => !takenNames.includes(letter)) ?? 'A' }));
      setTakenLetters(takenNames);
      return;
    }
    const currentWorkout = workouts.find((workout) => workout.id === workoutId);
    // An id that isn't one of the user's workouts was typed into the url: back to the list
    if (!currentWorkout) {
      navigate('/workouts');
      return;
    }
    const { attributes } = currentWorkout;
    setWorkoutData({
      name: attributes.name,
      description: attributes.description,
      image: attributes.image,
      motivation: attributes.motivation,
      voiceInstructions: attributes.voiceInstructions ?? true,
      exercises: (attributes.exercises || []).map((ex) =>
        withKey({ ...ex, videoUrl: ex.videoUrl ?? '', thumbnailUrl: ex.thumbnailUrl ?? '', gifUrl: ex.gifUrl ?? '', exerciseId: ex.exerciseId ?? '' })
      ),
      howTo: attributes.howTo,
    });
    // The workout being edited keeps its own letter even though it's "taken" by itself
    setTakenLetters(takenNames.filter((name) => name !== attributes.name));
  }, [isNew, navigate, takenLetters, workoutId, workouts]);

  // A workout kept locked (Pro ended) isn't edited: back to the list, which says why
  const isLockedWorkout = !!workoutId && lockedWorkoutIds(workouts, me).has(workoutId);
  useEffect(() => {
    if (!isLockedWorkout) return;
    toast.info(t('notifications.workoutLocked'), { toastId: 'workout-locked' });
    navigate('/workouts', { replace: true });
  }, [isLockedWorkout, navigate, t]);

  // AI or a split picked on the list's empty pane: Pro goes to its steps, a free account sees the offer (once the
  // account is known)
  const startHandled = useRef(false);
  useEffect(() => {
    if (startHandled.current || !me || (start !== 'ai' && start !== 'split')) return;
    startHandled.current = true;
    if (isPro) setBuilderStart(builderStepFor(start));
    else (start === 'split' ? splitOffer : aiOffer).onOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  // Closing the tab with unsaved changes asks the browser's own question
  useEffect(() => {
    if (!isDirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isDirty]);

  // Phones: the page under the library doesn't scroll
  const isLibraryPage = !isDesktop && !!libMode;
  useEffect(() => {
    if (!isLibraryPage) return undefined;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [isLibraryPage]);

  const edit = useCallback((update) => {
    setWorkoutData(update);
    setDirty(true);
  }, []);

  const updateExercise = useCallback(
    (index, fields) =>
      edit((prev) => {
        const exercises = [...prev.exercises];
        exercises[index] = { ...exercises[index], ...fields };
        return { ...prev, exercises };
      }),
    [edit]
  );

  const removeExercise = useCallback(
    (index) => {
      edit((prev) => ({ ...prev, exercises: prev.exercises.filter((_, i) => i !== index) }));
      setOpenKey(null);
    },
    [edit]
  );

  const toggleExercise = useCallback((key) => setOpenKey((open) => (open === key ? null : key)), []);

  const openLibrary = useCallback((mode) => {
    setLibMode(mode);
    setAddedCount(0);
  }, []);

  const swapExercise = useCallback((index) => {
    setSwapIndex(index);
    setLibMode('swap');
  }, []);

  const closeLibrary = useCallback(() => setLibMode(null), []);

  const pickFromLibrary = (attrs) => {
    const fields = libraryFields(attrs, isPt);
    if (libMode === 'swap') {
      // The new exercise takes the old one's place and numbers; a warm-up stays a warm-up
      edit((prev) => {
        const exercises = [...prev.exercises];
        const old = exercises[swapIndex];
        exercises[swapIndex] = { ...old, ...fields, name: old.warmup ? `${fields.name} (${t('components.ExerciseCard.markAsWarmup')})` : fields.name };
        return { ...prev, exercises };
      });
      setLibMode(null);
      return;
    }
    edit((prev) => ({ ...prev, exercises: [...prev.exercises, withKey({ ...newExercise(language), ...fields })] }));
    setAddedCount((count) => count + 1);
    setHasNoExercises(false);
  };

  // Not in the library: in by name, with no video or steps, and opened to set its numbers
  const addTyped = (name) => {
    const exercise = withKey({ ...newExercise(language), name });
    edit((prev) => ({ ...prev, exercises: [...prev.exercises, exercise] }));
    setOpenKey(exercise.uiKey);
    setHasNoExercises(false);
    setLibMode(null);
  };

  // Back to where the editor was opened from (the open workout, the list), or to the workout itself
  const leave = (to) => {
    if (location.state?.fromView || location.key !== 'default') navigate(-1);
    else navigate(to, { replace: true });
  };

  const askBeforeLeaving = (go) => {
    if (isDirty) setPendingLeave(() => go);
    else go();
  };

  const handleCancel = () => askBeforeLeaving(() => leave(isNew ? '/workouts' : `/workouts?w=${workoutId}`));

  const handleExportPDF = async () => {
    if (!actions.canExport()) return;
    trackEvent('workout_export_pdf', 'Workout - Export PDF', 'workout', true);
    setIsExportingPDF(true);
    try {
      const blob = await pdf(<WorkoutPDF workoutData={withoutKeys(workoutData)} language={language} />).toBlob();
      downloadBlob(blob, `${sanitizeFilename(workoutData.name)}.pdf`);
    } finally {
      setIsExportingPDF(false);
    }
  };

  const handleSave = async () => {
    if (!workoutData.description?.trim()) {
      toast.error(t('notifications.emptyWorkoutDesc'));
      // The toast alone left people looking for the field: it turns red and takes the cursor
      setDescriptionMissing(true);
      descriptionRef.current?.focus();
      descriptionRef.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      return;
    }
    if (!workoutData.exercises.length) {
      toast.error(editor('noExercises'));
      setHasNoExercises(true);
      return;
    }
    const unnamed = workoutData.exercises.find((exercise) => !exercise.name?.trim());
    if (unnamed) {
      toast.error(t('notifications.emptyExerciseName'));
      setMissingNames(true);
      setOpenKey(unnamed.uiKey);
      return;
    }

    setIsSaving(true);
    const workoutInput = withoutKeys(workoutData);
    // After a save, the open workout; back from it goes where the editor was opened from
    const hasHistory = location.key !== 'default';
    try {
      if (isNew) {
        trackEvent('workout_create', `Create Workout: ${workoutData?.description}`, 'workout', true);
        const result = await createWorkoutMutation({ variables: { workoutInput } });
        await refetchWorkouts();
        const newId = result?.data?.createWorkout?.data?.id;
        setDirty(false);
        toast.success(t('notifications.workoutCreated'), { toastId: 'workout-success', autoClose: 2000 });
        navigate(newId ? `/workouts?w=${newId}` : '/workouts', { replace: true, state: { fromList: hasHistory } });
      } else {
        trackEvent('workout_update', `Update Workout: ${workoutData?.description}`, 'workout', true);
        await updateWorkoutMutation({ variables: { workoutInput, workoutId } });
        await refetchWorkouts();
        setDirty(false);
        toast.success(t('notifications.workoutUpdated'), { toastId: 'workout-success', autoClose: 2000 });
        if (location.state?.fromView) navigate(-1);
        else navigate(`/workouts?w=${workoutId}`, { replace: true, state: { fromList: hasHistory } });
      }
    } catch (error) {
      // The API refuses a second workout on the free plan
      if (isNew && /plano gratuito|free plan/i.test(error?.message ?? '')) {
        aiOffer.onOpen();
      } else {
        toast.error(t('notifications.workoutSaveError'));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleRegenerate = () => {
    if (isPro) {
      trackEvent('workout_generate_ai_again', 'Workout - Generate AI Again', 'workout', true);
      setBuilderStart('muscles');
      setStage('build');
      return;
    }
    trackEvent('workout_generate_manual', 'Workout - Generate Manual (free user)', 'workout', true);
    setIsAIGenerated(false);
    setWorkoutData((prev) => ({ ...emptyWorkout(), name: prev.name }));
    setDirty(false);
    setLibMode('add');
  };

  // The builder's hand-offs
  const startFromScratch = () => {
    setIsAIGenerated(false);
    setStage('editor');
    openLibrary('add');
  };

  const takeGenerated = (generated) => {
    // The AI's reply never renames the workout: it keeps its letter
    setWorkoutData({ ...generated, name: workoutData.name, exercises: (generated.exercises || []).map(withKey) });
    setIsAIGenerated(true);
    setDirty(true);
    setOpenKey(null);
    setStage('editor');
  };

  const guardOpen = (event, row) => {
    if (!isDirty) return;
    event.preventDefault();
    setPendingLeave(() => () => navigate(`/workouts?w=${row.id}`));
  };

  const canExport = !!workoutId && workoutData.exercises.some((exercise) => exercise.name?.length > 0);
  const inWorkout = useMemo(() => new Set(workoutData.exercises.map((exercise) => exercise.exerciseId).filter(Boolean)), [workoutData.exercises]);

  const libraryPane = libMode && (
    <ExerciseLibraryPane
      mode={libMode}
      letter={workoutData.name}
      inWorkout={inWorkout}
      addedCount={addedCount}
      total={workoutData.exercises.length}
      exercises={library.exercises}
      isLoading={library.isLoading}
      isDesktop={isDesktop}
      onPick={pickFromLibrary}
      onAddTyped={addTyped}
      onClose={closeLibrary}
    />
  );

  let body;
  if (stage === 'build') {
    body = (
      <>
        {!isDesktop && (
          <header className="mt-tr-bar mt-tr-bar--title">
            <button type="button" className="mt-tr-bar__back" onClick={handleCancel} aria-label={t('pages.Workout.editor.back')}>
              <ArrowLeft aria-hidden="true" />
            </button>
            <h1 className="mt-tr-bar__title">{editor('titleNew')}</h1>
            <span className="mt-tr-bar__end" />
          </header>
        )}
        <WorkoutBuilder
          key={builderStart}
          initialStep={builderStart}
          isPro={isPro}
          letter={workoutData.name}
          count={workouts?.length ?? 0}
          limit={limit}
          workoutData={withoutKeys(workoutData)}
          onManual={startFromScratch}
          onGenerated={takeGenerated}
          onSplitReady={(generatedWorkouts) => navigate('/split-review', { state: { generatedWorkouts } })}
          onProRequired={(type) => (type === 'split' ? splitOffer.onOpen() : aiOffer.onOpen())}
          onSplitLimitExceeded={(toDelete, splitCount) => {
            setSplitLimitInfo({ toDelete, splitCount });
            splitLimit.onOpen();
          }}
          onCancel={isDesktop ? undefined : handleCancel}
        />
      </>
    );
  } else if (isLoading || !takenLetters) {
    body = (
      <div className="mt-tr-editor" aria-busy="true">
        {[0, 1, 2, 3].map((index) => (
          <span key={index} className="mt-tr-skel mt-tr-skel--row" />
        ))}
      </div>
    );
  } else {
    body = (
      <WorkoutEditorView
        isNew={isNew}
        isDesktop={isDesktop}
        workoutData={workoutData}
        takenLetters={takenLetters}
        isSaving={isSaving}
        isExporting={isExportingPDF}
        canExport={canExport}
        isAIGenerated={isAIGenerated}
        isPro={isPro}
        trialContext={trialContext(me)}
        openKey={openKey}
        library={library.exercises}
        descriptionRef={descriptionRef}
        descriptionMissing={descriptionMissing && !workoutData.description?.trim()}
        missingNames={missingNames}
        hasNoExercises={hasNoExercises && !workoutData.exercises.length}
        onLetterChange={(name) => edit((prev) => ({ ...prev, name }))}
        onFieldChange={(field, value) => edit((prev) => ({ ...prev, [field]: value }))}
        onToggle={toggleExercise}
        onExerciseChange={updateExercise}
        onExerciseRemove={removeExercise}
        onSwap={swapExercise}
        onOpenLibrary={openLibrary}
        onExportPdf={handleExportPDF}
        onSave={handleSave}
        onCancel={handleCancel}
        onRegenerate={handleRegenerate}
        onSubscribe={() => {
          trackEvent('workout_subscribe_to_pro', 'Workout - Subscribe to Pro CTA', 'workout', true);
          navigate('/plans');
        }}
      />
    );
  }

  return (
    <div className={`mt-root mt-root--app mt-tr-page${stage === 'editor' ? ' mt-tr-page--editor' : ' mt-tr-page--build'}`}>
      {actions.modals}
      <ModalUpgradePro
        isOpen={aiOffer.isOpen}
        onOpenChange={aiOffer.onOpenChange}
        onClose={aiOffer.onClose}
        title={t('pages.Workout.proRequiredBanner')}
        description={t('pages.Workout.proRequiredDesc')}
        videoSrc={language === 'pt' ? saveAiCustomVidBr : saveAiCustomVidEn}
      />
      <ModalUpgradePro
        isOpen={splitOffer.isOpen}
        onOpenChange={splitOffer.onOpenChange}
        onClose={splitOffer.onClose}
        title={t('pages.Workout.proRequiredSplitBanner')}
        description={t('pages.Workout.proRequiredSplitDesc')}
        videoSrc={language === 'pt' ? saveAiSplitVidBr : saveAiSplitVidEn}
      />
      <Modal
        isOpen={splitLimit.isOpen}
        onOpenChange={splitLimit.onOpenChange}
        placement="center"
        hideCloseButton
        classNames={{ base: 'mt-root mt-dialog', backdrop: 'mt-dialog__backdrop' }}
      >
        <ModalContent>
          {() => (
            <div className="mt-dialog__body">
              <h2 className="mt-dialog__title">{t('pages.Workout.splitLimitTitle')}</h2>
              <p className="mt-dialog__text">
                {t('pages.Workout.splitLimitDesc', { toDelete: splitLimitInfo?.toDelete, splitCount: splitLimitInfo?.splitCount })}
              </p>
              <div className="mt-dialog__actions">
                <button type="button" className="mt-btn mt-btn--quiet" onClick={splitLimit.onClose}>
                  {t('pages.Workout.splitLimitChooseAnother')}
                </button>
                <button
                  type="button"
                  className="mt-btn mt-btn--primary"
                  onClick={() => {
                    splitLimit.onClose();
                    navigate('/workouts');
                  }}
                >
                  {t('pages.Workout.splitLimitDeleteCta')}
                </button>
              </div>
            </div>
          )}
        </ModalContent>
      </Modal>
      <Modal
        isOpen={!!pendingLeave}
        onOpenChange={(open) => !open && setPendingLeave(null)}
        placement="center"
        hideCloseButton
        classNames={{ base: 'mt-root mt-dialog', backdrop: 'mt-dialog__backdrop' }}
      >
        <ModalContent>
          {(onClose) => (
            <div className="mt-dialog__body">
              <h2 className="mt-dialog__title">{editor('discardTitle')}</h2>
              <p className="mt-dialog__text">{editor('discardBody')}</p>
              <div className="mt-dialog__actions">
                <button type="button" className="mt-btn mt-btn--quiet" onClick={onClose}>
                  {editor('keepEditing')}
                </button>
                <button
                  type="button"
                  className="mt-btn mt-btn--danger"
                  onClick={() => {
                    const go = pendingLeave;
                    setPendingLeave(null);
                    setDirty(false);
                    go();
                  }}
                >
                  {editor('discard')}
                </button>
              </div>
            </div>
          )}
        </ModalContent>
      </Modal>

      <div className="mt-tr-layout">
        {isDesktop &&
          (libraryPane || (
            <WorkoutsListPane
              rows={rows}
              count={usable.length}
              limit={limit}
              isPro={isPro}
              isLoading={isLoading}
              isDesktop
              currentId={workoutId}
              isExportingAll={actions.isExportingAll}
              unlockLabel={actions.unlockLabel}
              onNew={() => askBeforeLeaving(() => actions.handleNew())}
              onExportAll={actions.handleExportAll}
              onUnlock={actions.handleUnlock}
              onOpen={guardOpen}
            />
          ))}
        {isDesktop ? (
          <section className={`mt-tr-pane mt-tr-detail${stage === 'build' ? ' mt-tr-detail--short' : ''}`} aria-label={editor(isNew ? 'titleNew' : 'titleEdit')}>
            {body}
          </section>
        ) : (
          body
        )}
      </div>
      {!isDesktop && libraryPane}
    </div>
  );
};

// A new workout starts fresh on every visit; another workout's id is another editor
export const Workout = () => {
  const { workoutId } = useParams();
  const location = useLocation();
  return <WorkoutPage key={workoutId ?? `new-${location.key}`} />;
};
